import { create } from 'zustand'
import {
  clearTabPathAliases,
  forgetTabPath,
  noteTabPathReplaced,
  resolveLiveTabPath,
} from './tabPathAliases'

export type TabKind = 'list' | 'record' | 'report' | 'print' | 'mail'
export type TabPresentation = 'tab' | 'modal' | 'unresolved'

export interface AppTab {
  /** Identity AND location: the router path this tab represents (incl. ?form=). */
  path: string
  /**
   * Stable React key for the host surface. Survives `/new` → `/id` rename so
   * Save does not remount the form (path alone as `key` caused a full flicker).
   */
  surfaceKey: string
  kind: TabKind
  title: string
  /** Document Posted/Draft — shown on the tab, not in the form title row. */
  status?: string
  isDirty: boolean
  presentation: TabPresentation
}

/** Callers may omit `surfaceKey` — `openTab` / `hydrate` mint one. */
export type AppTabInput = Omit<AppTab, 'surfaceKey'> & { surfaceKey?: string }

function newSurfaceKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `sk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function withSurfaceKey(tab: AppTabInput): AppTab {
  return {
    ...tab,
    surfaceKey: tab.surfaceKey || newSurfaceKey(),
  }
}

export const MAX_APP_TABS = 15

/**
 * True when the tab already holds this value, so the setter can skip `set()`.
 *
 * Load-bearing, not an optimisation. The host passes inline arrows to each tab
 * body, so every store update hands the body a NEW callback identity; a body
 * effect that depends on that callback (`onDirtyChange`, `onTitleChange`) then
 * re-fires and calls the setter again. An unguarded setter always builds a
 * fresh `tabs` array, so that cycle never settles — React error #185, and the
 * form never appears.
 */
function unchanged<K extends keyof AppTab>(
  tabs: AppTab[],
  path: string,
  key: K,
  value: AppTab[K],
): boolean {
  const tab = tabs.find((t) => t.path === path)
  return tab == null || tab[key] === value
}

export type OpenTabResult = { ok: true } | { ok: false; reason: 'limit' }

interface AppWorkspaceState {
  tabs: AppTab[]
  activePath: string | null
  /** Where to return when the last tab closes. */
  returnPath: string | null

  openTab: (tab: AppTabInput) => OpenTabResult
  closeTab: (path: string) => void
  setActive: (path: string | null) => void
  setDirty: (path: string, isDirty: boolean) => void
  setTitle: (path: string, title: string) => void
  setStatus: (path: string, status: string | undefined) => void
  /** New record saved: /app/X/new → /app/X/<id>. Same tab, never forks. */
  replacePath: (oldPath: string, newPath: string) => void
  setPresentation: (path: string, presentation: TabPresentation) => void
  setReturnPath: (path: string | null) => void
  hydrate: (tabs: AppTabInput[], activePath: string | null) => void
  reset: () => void
}

export const useAppWorkspaceStore = create<AppWorkspaceState>((set, get) => ({
  tabs: [],
  activePath: null,
  returnPath: null,

  openTab: (tab) => {
    const state = get()
    const exists = state.tabs.find((t) => t.path === tab.path)
    if (exists) {
      // Hydrated list/report tabs used to stick on 'unresolved' (hidden, no strip).
      const promote =
        exists.presentation === 'unresolved' &&
        (exists.kind === 'list' || exists.kind === 'report')
      if (!promote && state.activePath === tab.path) {
        return { ok: true }
      }
      set({
        activePath: tab.path,
        tabs: promote
          ? state.tabs.map((t) =>
              t.path === tab.path ? { ...t, presentation: 'tab' } : t,
            )
          : state.tabs,
      })
      return { ok: true }
    }
    if (state.tabs.length >= MAX_APP_TABS) {
      return { ok: false, reason: 'limit' }
    }
    // A prior save left `/new → /id` in the alias map; reclaiming `/new` for a
    // brand-new create must drop that edge or Close resolves to a dead path.
    forgetTabPath(tab.path)
    const next = withSurfaceKey(tab)
    set({
      tabs: [...state.tabs, next],
      activePath: next.path,
    })
    return { ok: true }
  },

  closeTab: (path) => {
    set((state) => {
      const idx = state.tabs.findIndex((t) => t.path === path)
      if (idx < 0) return state
      forgetTabPath(path)
      const tabs = state.tabs.filter((t) => t.path !== path)
      const nextActive =
        state.activePath === path
          ? tabs[idx]?.path ?? tabs[idx - 1]?.path ?? null
          : state.activePath
      return { tabs, activePath: nextActive }
    })
  },

  setActive: (activePath) => {
    if (get().activePath === activePath) return
    set({ activePath })
  },

  setDirty: (path, isDirty) => {
    // Closures often still hold the pre-save path after replacePath
    // (/new → /id). Resolve so a post-save dirty clear cannot miss the tab.
    const live = resolveLiveTabPath(path)
    if (unchanged(get().tabs, live, 'isDirty', isDirty)) return
    set((state) => ({
      tabs: state.tabs.map((t) => (t.path === live ? { ...t, isDirty } : t)),
    }))
  },

  setTitle: (path, title) => {
    if (unchanged(get().tabs, path, 'title', title)) return
    set((state) => ({
      tabs: state.tabs.map((t) => (t.path === path ? { ...t, title } : t)),
    }))
  },

  setStatus: (path, status) => {
    const live = resolveLiveTabPath(path)
    if (unchanged(get().tabs, live, 'status', status)) return
    set((state) => ({
      tabs: state.tabs.map((t) => (t.path === live ? { ...t, status } : t)),
    }))
  },

  replacePath: (oldPath, newPath) => {
    set((state) => {
      if (!state.tabs.some((t) => t.path === oldPath)) return state
      // Callbacks captured before the rename must still find this tab.
      noteTabPathReplaced(oldPath, newPath)
      const tabs = state.tabs.map((t) =>
        t.path === oldPath ? { ...t, path: newPath } : t,
      )
      return {
        tabs,
        activePath: state.activePath === oldPath ? newPath : state.activePath,
      }
    })
  },

  setPresentation: (path, presentation) => {
    if (unchanged(get().tabs, path, 'presentation', presentation)) return
    set((state) => ({
      tabs: state.tabs.map((t) =>
        t.path === path ? { ...t, presentation } : t,
      ),
    }))
  },

  setReturnPath: (returnPath) => set({ returnPath }),

  hydrate: (tabs, activePath) =>
    set({ tabs: tabs.map(withSurfaceKey), activePath }),

  reset: () => {
    clearTabPathAliases()
    set({ tabs: [], activePath: null, returnPath: null })
  },
}))
